import React, { createContext, useState, useContext,useEffect,useRef,ReactNode } from 'react';
import { BizMaster, BizSettings, CustomRole, RoleAccess, SubscriptionData } from '../../types';
import { CurrencyList } from '../../DS-Files';

// Define a type for the context state
type AppContextType = {
  sharedState: string;
  bizMaster?:BizMaster;
  bizSettings?:BizSettings;
  roleAccess?: RoleAccess;
  customRoles: CustomRole[];
  subscriptionData?: SubscriptionData;
  setSharedState: (value: string) => void;
  setBizMaster: (value: BizMaster) => void;
  setBizSettings:(value:BizSettings) => void;
  setRoleAccess: (value: RoleAccess) => void;
  setCustomRoles: (value: CustomRole[]) => void;
  setSubscriptionData: (value: SubscriptionData | undefined) => void;
  getLocale:()=> string;
  resetContext: () => void; // Reset function to clear context on logout
};

export let globalValue:any = {}

export const getCurrencySymbol = () : string =>{
  const baseCurrency = globalValue.bizMaster.bizDetails.currency || 'INR'
  const currencySymbol = CurrencyList.find((currency)=> currency.currencyID === baseCurrency)?.symbol
  return currencySymbol || '$'
}

export const getCurrency= () : string =>{
  const baseCurrency = globalValue?.bizMaster?.bizDetails?.currency || 'INR'
  return baseCurrency
}

// Returns BCP-47 locale derived from the business's configured country.
// Falls back to browser locale so it works before bizMaster is loaded.
export const getBusinessLocale = (): string => {
  const countryCode = globalValue?.bizMaster?.bizDetails?.country;
  return countryCode ? `en-${countryCode}` : (navigator.language || 'en-US');
}

// Create the context with a default value (can be null)
const AppContext = createContext<AppContextType | undefined>(undefined);

// Create a provider component
export const AppProvider = ({ children }: { children: ReactNode }) => {
  const initialState = 'Initial value';
  const [sharedState, setSharedState] = useState<string>(initialState);
  const [bizMaster, setBizMaster] = useState<BizMaster>();
  const [bizSettings, setBizSettings] = useState<BizSettings>();
  const [roleAccess, setRoleAccess] = useState<RoleAccess>();
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | undefined>();
  // Function to reset the context state to initial values
  const resetContext = () => {
    setSharedState(initialState);
    globalValue = {}
    setBizMaster(undefined)
    setBizSettings(undefined)
    setRoleAccess(undefined)
    setCustomRoles([])
    setSubscriptionData(undefined)
  };

  const getLocale = (): string => {
    const countryCode = bizMaster?.bizDetails?.country;
    // Return the locale if countryCode exists, otherwise fallback to 'en-US'
    return countryCode ? 'en-'+countryCode : 'en-US';
  };

  return (
    <AppContext.Provider value={{ sharedState, bizMaster, bizSettings, roleAccess, customRoles, subscriptionData, setSharedState, setBizMaster, setBizSettings, setRoleAccess, setCustomRoles, setSubscriptionData, resetContext, getLocale }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the AppContext
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};



const readPersistedState = <T,>(key: string, initialValue: T): T => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue !== null ? (JSON.parse(storedValue) as T) : initialValue;
  } catch (error) {
    console.error("Error reading localStorage:", error);
    return initialValue;
  }
};

export const usePersistentState = <T,>(key: string, initialValue: T) => {
  // Read synchronously on first mount so callers never see the default value
  // for one render before the persisted value replaces it — that stale-then-correct
  // transition previously fired any effect depending on `state` twice (e.g. an initial
  // data fetch with default filters immediately followed by a refetch with the real ones).
  const [state, setState] = useState<T>(() => readPersistedState(key, initialValue));
  const keyRef = useRef(key);

  // If `key` changes without the component remounting (e.g. navigating between
  // records that share this component), adjust state during render rather than in
  // an effect — React re-renders with the corrected value before any effect keyed
  // on `state` runs, so downstream consumers still only see one, correct value.
  if (keyRef.current !== key) {
    keyRef.current = key;
    const next = readPersistedState(key, initialValue);
    if (JSON.stringify(next) !== JSON.stringify(state)) {
      setState(next);
    }
  }

  // Save state to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }, [key, state]);

  return { state, setState };
};

